import { SegmentedControl } from '@mantine/core';
import { useRouter } from 'next/router';
import type { ViewMode } from '~/atoms/atoms';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useViewMode } from '~/hooks/context/useViewMode';

export function ViewModeSwitcher({ fullWidth }: { fullWidth?: boolean }) {
  const projectId = useProjectId();
  const [viewMode, setViewMode] = useViewMode(projectId);
  const { orgMetadata } = useAuthData();
  const router = useRouter();

  // Agent 2.0 is now available for all organizations
  const canAccessAgentV2 = true;

  if (!canAccessAgentV2) {
    return null;
  }

  const isAgentLockedOrg = orgMetadata?.defaultViewMode === 'agent';

  if (isAgentLockedOrg) {
    return null;
  }

  const handleViewModeChange = async (value: string) => {
    if (value !== 'classic' && value !== 'agent') {
      return;
    }
    const newMode: ViewMode = value;

    // Update the atom directly to ensure state is in sync
    // This handles the case where user is already on the target route
    setViewMode(newMode);

    if (!projectId) {
      return;
    }

    const targetPath = newMode === 'classic' ? `/project/${projectId}` : `/project/${projectId}/agents`;
    try {
      await router.push(targetPath);
    } catch (error) {
      console.error(`Failed to navigate to ${newMode} view:`, error);
    }
  };

  return (
    <SegmentedControl
      value={viewMode}
      onChange={handleViewModeChange}
      data={[
        { label: 'Classic', value: 'classic' },
        { label: 'Agent 2.0', value: 'agent' },
      ]}
      size="xs"
      fullWidth={fullWidth}
      className="bg-gray-100 dark:bg-gray-800"
    />
  );
}
